import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogBody, DialogDescription, DialogFooter, DialogHeader, DialogScrollContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreVertical, Edit3, Trash2, EyeOff, Eye } from "lucide-react";
import LessonArticleEditor from "@/components/admin/LessonArticleEditor";
import { LessonInsertSchema } from "@/lib/schemas";
import { listLessons, createLesson, updateLesson, type LessonRow } from "@/integrations/backend/lessons";
import { motivationOptions, labels } from "@/lib/onboarding-options";
import { stripInlineFontSize } from "@/lib/html";
import { Checkbox } from "@/components/ui/checkbox";

const lessonsAdminListKey = queryKeys.lessons.list(true);

export default function LessonsView() {
  const queryClient = useQueryClient();
  const lessonsQuery = useQuery({
    queryKey: lessonsAdminListKey,
    queryFn: () => listLessons(true),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...lessonsAdminListKey]),
  });
  const lessons = lessonsQuery.data ?? [];
  const loading = lessonsQuery.isLoading;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LessonRow | null>(null);
  const [lessonStatusTab, setLessonStatusTab] = useState<"active" | "deactivated">("active");

  const [title, setTitle] = useState("");
  const [motivations, setMotivations] = useState<string[]>([]);
  const [shortDescription, setShortDescription] = useState("");
  const [articleHtml, setArticleHtml] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [lessonNumber, setLessonNumber] = useState<number>(1);
  const [disabled, setDisabled] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [infographicFile, setInfographicFile] = useState<File | null>(null);
  const [infographicPreview, setInfographicPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const activeCount = useMemo(() => lessons.filter(l => !l.is_disabled).length, [lessons]);
  const visibleLessons = useMemo(
    () => lessons.filter((l) => (lessonStatusTab === "deactivated" ? l.is_disabled : !l.is_disabled)),
    [lessons, lessonStatusTab]
  );

  function getVideoEmbed(url: string): { type: "iframe"; src: string } | null {
    const yt = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/i.exec(url);
    if (yt?.[1]) {
      return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
    }
    const vimeo = /vimeo\.com\/(\d+)/i.exec(url);
    if (vimeo?.[1]) {
      return { type: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}` };
    }
    return null;
  }

  const refetchLessons = () => queryClient.invalidateQueries({ queryKey: lessonsAdminListKey });

  const toggleMotivation = (motivationId: string, checked: boolean) => {
    setMotivations((prev) => {
      const next = new Set(prev);
      if (checked) next.add(motivationId);
      else next.delete(motivationId);
      return Array.from(next);
    });
  };

  const selectAllMotivations = () => setMotivations(motivationOptions.map((m) => m.id));
  const clearMotivations = () => setMotivations([]);

  const openCreate = () => {
    setEditing(null);
    setTitle("");
    setMotivations([]);
    setShortDescription("");
    setArticleHtml("");
    setVideoUrl("");
    setLessonNumber(activeCount + 1);
    setDisabled(false);
    setImageFile(null);
    setImagePreview(null);
    setInfographicFile(null);
    setInfographicPreview(null);
    setDialogOpen(true);
  };

  const openEdit = (row: LessonRow) => {
    setEditing(row);
    setTitle(row.title);
    setMotivations(Array.isArray(row.motivations) ? row.motivations : []);
    setShortDescription(row.short_description || "");
    setArticleHtml(stripInlineFontSize(row.article ?? ""));
    setVideoUrl(row.video_url || "");
    setLessonNumber(row.lesson_number);
    setDisabled(Boolean(row.is_disabled));
    setImageFile(null);
    // Show existing thumbnail image as preview if available
    setImagePreview(row.thumbnail_file || null);
    setInfographicFile(null);
    // Show existing infographic image as preview if available
    setInfographicPreview(row.infographic_file || null);
    setDialogOpen(true);
  };

  const onSave = async () => {
    if (!title.trim() || motivations.length === 0 || !shortDescription.trim() || !articleHtml.trim()) return;
    setSaving(true);
    const sanitizedArticleHtml = stripInlineFontSize(articleHtml);
    try {
      if (editing) {
        await updateLesson(
          editing.id,
          {
            title: title.trim(),
            motivations,
            short_description: shortDescription.trim(),
            article: sanitizedArticleHtml,
            video_url: videoUrl.trim() || null,
            lesson_number: lessonNumber,
            is_disabled: disabled,
          },
          imageFile || undefined,
          infographicFile || undefined
        );
      } else {
        const input = LessonInsertSchema.parse({
          title: title.trim(),
          motivations,
          short_description: shortDescription.trim(),
          article: sanitizedArticleHtml,
          video_url: videoUrl.trim() ? videoUrl.trim() : null,
          lesson_number: lessonNumber,
          is_disabled: false,
        });
        await createLesson(input, imageFile || undefined, infographicFile || undefined);
      }
      setDialogOpen(false);
      await refetchLessons();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const toggleDisabled = async (row: LessonRow) => {
    try {
      await updateLesson(row.id, { is_disabled: !row.is_disabled } as any);
      await refetchLessons();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Lessons</h1>
          <p className="text-gray-600">Create and manage educational content.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(v)=>{ setDialogOpen(v); if(!v){ setEditing(null);} }}>
          <DialogTrigger asChild>
            <Button onClick={openCreate}>Create Lesson</Button>
          </DialogTrigger>
          <DialogScrollContent className="sm:max-w-[1000px] bg-white text-black">
            <DialogHeader className="shrink-0">
              <DialogTitle>{editing ? "Edit Lesson" : "Create Lesson"}</DialogTitle>
              <DialogDescription>Compose the article, optionally add a video URL and thumbnail image.</DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Title</Label>
                  <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="e.g., Perfect Your Squat" />
                </div>
                <div className="space-y-1">
                  <Label>Display Order</Label>
                  <Input type="number" value={lessonNumber} onChange={(e)=>setLessonNumber(parseInt(e.target.value || "1", 10))} min={1} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label>Motivations</Label>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={selectAllMotivations} className="h-8 px-2 text-xs text-black border-gray-300 hover:bg-gray-100">
                        Select all
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={clearMotivations} className="h-8 px-2 text-xs text-black border-gray-300 hover:bg-gray-100">
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-gray-200 p-3">
                    {motivationOptions.map((m) => {
                      const selected = motivations.includes(m.id);
                      return (
                        <label key={m.id} className="flex items-center gap-2 text-sm">
                          <Checkbox checked={selected} onCheckedChange={(checked) => toggleMotivation(m.id, Boolean(checked))} />
                          <span>{m.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Short description</Label>
                  <Input
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value)}
                    placeholder="Shown on lesson cards"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Article</Label>
                <LessonArticleEditor
                  value={articleHtml}
                  onChange={setArticleHtml}
                />
              </div>
              <div className="space-y-1">
                <Label>Video URL (optional)</Label>
                <Input value={videoUrl} onChange={(e)=>setVideoUrl(e.target.value)} placeholder="https://..." />
              </div>
              <div className="space-y-1">
                <Label>Thumbnail Image (optional)</Label>
                {imagePreview && (
                  <img src={imagePreview} alt="Thumbnail Preview" className="h-32 w-full object-cover rounded-md border mb-2" />
                )}
                <Input type="file" accept="image/*" onChange={(e)=>{
                  const f = e.target.files?.[0] || null;
                  setImageFile(f);
                  setImagePreview(f ? URL.createObjectURL(f) : (editing?.thumbnail_file || null));
                }} />
              </div>
              <div className="space-y-1">
                <Label>Infographic Image (optional)</Label>
                {infographicPreview && (
                  <img src={infographicPreview} alt="Infographic Preview" className="h-32 w-full object-cover rounded-md border mb-2" />
                )}
                <Input type="file" accept="image/*" onChange={(e)=>{
                  const f = e.target.files?.[0] || null;
                  setInfographicFile(f);
                  setInfographicPreview(f ? URL.createObjectURL(f) : (editing?.infographic_file || null));
                }} />
              </div>
            </DialogBody>
            <DialogFooter className="shrink-0">
              <Button variant="outline" onClick={()=>setDialogOpen(false)} className="text-black border-gray-300 hover:bg-gray-100">Cancel</Button>
              <Button onClick={onSave} disabled={saving || !title.trim() || motivations.length === 0 || !shortDescription.trim() || !articleHtml.trim()}>{saving ? "Saving..." : "Save"}</Button>
            </DialogFooter>
          </DialogScrollContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-4 border-b border-gray-200 pb-2">
        <button
          type="button"
          onClick={() => setLessonStatusTab("active")}
          className={`px-4 py-2 font-medium transition-colors ${
            lessonStatusTab === "active"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Active Lessons
        </button>
        <button
          type="button"
          onClick={() => setLessonStatusTab("deactivated")}
          className={`px-4 py-2 font-medium transition-colors ${
            lessonStatusTab === "deactivated"
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Deactivated Lessons
        </button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Lessons</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading && (
              <div className="col-span-full text-center text-sm text-gray-600">Loading lessons…</div>
            )}
            {!loading && visibleLessons.map((l) => (
              <div key={l.id} className="group relative rounded-xl overflow-hidden border bg-white">
                <div className="relative h-40 w-full bg-gray-100 flex items-center justify-center">
                  {(() => {
                    // thumbnail_file and infographic_file are already URLs from the backend
                    if (l.thumbnail_file) {
                      return (
                        <button
                          type="button"
                          onClick={() => { setPreviewSrc(l.thumbnail_file!); setPreviewOpen(true); }}
                          className="absolute inset-0"
                          aria-label="Open image preview"
                        >
                          <img
                            src={l.thumbnail_file}
                            alt={l.title}
                            className="h-full w-full object-cover"
                          />
                        </button>
                      );
                    }
                    if (l.video_url) {
                      const emb = getVideoEmbed(l.video_url);
                      if (emb) {
                        return (
                          <div className="absolute inset-0">
                            <iframe
                              src={emb.src}
                              title={l.title}
                              className="w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(l.video_url!, "_blank", "noopener,noreferrer");
                              }}
                            />
                          </div>
                        );
                      }
                      return (
                        <video
                          src={l.video_url}
                          className="absolute inset-0 w-full h-full object-cover"
                          controls
                          onClick={() => window.open(l.video_url!, "_blank", "noopener,noreferrer")}
                        />
                      );
                    }
                    return <span className="text-gray-500">Lesson #{l.lesson_number}</span>;
                  })()}
                  <div className="absolute top-3 right-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" aria-label="Actions">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white text-black">
                        <DropdownMenuItem onClick={()=>openEdit(l)}>
                          <Edit3 className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={()=>toggleDisabled(l)}>
                          {l.is_disabled ? (<><Eye className="w-4 h-4 mr-2" /> Enable</>) : (<><EyeOff className="w-4 h-4 mr-2" /> Disable</>)}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {l.is_disabled && (
                    <div className="absolute top-3 left-3">
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200">Disabled</Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-base font-semibold text-gray-900 line-clamp-1">{l.title}</div>
                      {Array.isArray(l.motivations) && l.motivations.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {l.motivations.map((id) => (
                            <Badge key={id} variant="outline" className="text-xs border-gray-300 text-gray-800 bg-gray-50">
                              {labels.motivation?.[id] || id}
                            </Badge>
                          ))}
                        </div>
                      ) : null}
                      {l.short_description && (
                        <div className="text-xs text-gray-700 mt-2 line-clamp-3">
                          {l.short_description}
                        </div>
                      )}
                      {l.video_url && (
                        <div className="text-xs text-gray-600 mt-1 truncate">Video: {l.video_url}</div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </div>
            ))}
            {(!loading && visibleLessons.length === 0) && (
              <div className="col-span-full text-center text-sm text-gray-500">
                {lessonStatusTab === "deactivated" ? "No deactivated lessons." : "No active lessons yet."}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      <DialogScrollContent className="bg-white text-black max-w-[90vw] max-h-[90vh]">
        <DialogBody>
          {previewSrc && (
            <img
              src={previewSrc}
              alt="Preview"
              className="w-full h-auto object-contain"
            />
          )}
        </DialogBody>
      </DialogScrollContent>
    </Dialog>
    </div>
  );
}


